import { useState, useEffect } from "react";
import CopyCode from "../../CopyCode/CopyCode";
import Icon from "../../Icon";

const SuccessMessage = () => {
  const [showSuccesses, setShowSuccesses] = useState({});
  const [timers, setTimers] = useState({});

  const triggerSuccess = (id) => {
    setShowSuccesses(prev => ({ ...prev, [id]: true }));
    
    if (timers[id]) clearTimeout(timers[id]);
    const timer = setTimeout(() => {
      setShowSuccesses(prev => ({ ...prev, [id]: false }));
    }, 3000);
    setTimers(prev => ({ ...prev, [id]: timer }));
  };

  return (
    <div className="flex flex-col gap-y-10 p-6">
      {/* SECTION 1: SUCCESS TYPES */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-white mb-6">Success Types</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Variation 1: Simple Success */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Simple Success</label>
            <button onClick={() => triggerSuccess('success1')} className="w-full px-3 py-2 bg-green-600 hover:bg-green-500 text-sm font-semibold text-white rounded mb-2">Complete Action</button>
            {showSuccesses['success1'] && (
              <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg flex items-start gap-3">
                <span className="text-2xl flex-shrink-0"><Icon name="check" className="w-6 h-6" /></span>
                <div>
                  <h4 className="font-semibold text-green-300">Success!</h4>
                  <p className="text-sm text-green-200 mt-1">Your changes have been saved.</p>
                </div>
              </div>
            )}
            <CopyCode number_code={55} code={`{success && (
  <div className="p-4 bg-green-900/20 
    border border-green-700/50 rounded-lg flex gap-3">
    <span className="text-2xl">Icon</span>
    <div>
      <h4 className="font-semibold text-green-300">
        Success!
      </h4>
      <p className="text-sm text-green-200 mt-1">
        Your changes saved.
      </p>
    </div>
  </div>
)}`} />
          </div>

          {/* Variation 2: With Details */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">With Details</label>
            <button onClick={() => triggerSuccess('success2')} className="w-full px-3 py-2 bg-green-600 hover:bg-green-500 text-sm font-semibold text-white rounded mb-2">Upload File</button>
            {showSuccesses['success2'] && (
              <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
                <h4 className="font-semibold text-green-300 mb-2 inline-flex items-center gap-2"><Icon name="check" className="w-4 h-4" /> Upload Complete</h4>
                <div className="space-y-1 text-sm text-green-200">
                  <p>- File: document.pdf</p>
                  <p>- Size: 2.4 MB</p>
                  <p>- Duration: 4.2 seconds</p>
                </div>
              </div>
            )}
            <CopyCode number_code={56} code={`<div className="p-4 bg-green-900/20 rounded">
  <h4 className="font-semibold text-green-300">
    Upload Complete
  </h4>
  <div className="space-y-1 text-sm 
    text-green-200 mt-2">
    <p>- File: document.pdf</p>
    <p>- Size: 2.4 MB</p>
  </div>
</div>`} />
          </div>

          {/* Variation 3: With Next Action */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">With Next Action</label>
            <button onClick={() => triggerSuccess('success3')} className="w-full px-3 py-2 bg-green-600 hover:bg-green-500 text-sm font-semibold text-white rounded mb-2">Create Account</button>
            {showSuccesses['success3'] && (
              <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
                <h4 className="font-semibold text-green-300 mb-2 inline-flex items-center gap-2"><Icon name="check" className="w-4 h-4" /> Account Created</h4>
                <p className="text-sm text-green-200 mb-4">Welcome! Your account is ready to use.</p>
                <button className="w-full px-3 py-2 bg-green-600 hover:bg-green-500 text-xs font-semibold text-white rounded">Start Tour</button>
              </div>
            )}
            <CopyCode number_code={57} code={`<div className="p-4 bg-green-900/20 rounded">
  <h4 className="font-semibold text-green-300">
    Account Created
  </h4>
  <p className="text-sm text-green-200 mt-2 mb-4">
    Welcome! Ready to use.
  </p>
  <button className="w-full px-3 py-2 
    bg-green-600 text-white rounded">
    Start Tour
  </button>
</div>`} />
          </div>

          {/* Variation 4: With Timer */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Auto-dismiss (Timer)</label>
            <button onClick={() => triggerSuccess('success4')} className="w-full px-3 py-2 bg-green-600 hover:bg-green-500 text-sm font-semibold text-white rounded mb-2">Send Email</button>
            {showSuccesses['success4'] && (
              <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg animate-fade-out">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-green-300 inline-flex items-center gap-2"><Icon name="check" className="w-4 h-4" /> Email Sent</h4>
                    <p className="text-sm text-green-200 mt-1">Message delivered successfully.</p>
                  </div>
                  <span className="text-xs text-green-300 font-bold">3s</span>
                </div>
              </div>
            )}
            <CopyCode number_code={58} code={`const [show, setShow] = useState(false);

useEffect(() => {
  if (show) {
    const timer = setTimeout(() => 
      setShow(false), 3000);
    return () => clearTimeout(timer);
  }
}, [show]);

{show && (
  <div className="p-4 bg-green-900/20 rounded">
    <h4>Email Sent</h4>
  </div>
)}`} />
          </div>

        </div>
      </div>

      {/* SECTION 2: SUCCESS VARIANTS */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-amber-400 mb-6">Success Variants</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Variation 5: Inline Success */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Inline Success</label>
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-2 bg-green-900/20 border border-green-700/50 rounded">
                <span className="text-green-400 font-bold"><Icon name="check" className="w-4 h-4" /></span>
                <span className="text-sm text-green-300">Email verified</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-green-900/20 border border-green-700/50 rounded">
                <span className="text-green-400 font-bold"><Icon name="check" className="w-4 h-4" /></span>
                <span className="text-sm text-green-300">Profile updated</span>
              </div>
            </div>
            <CopyCode number_code={59} code={`<div className="space-y-2">
  <div className="flex items-center gap-2 
    p-2 bg-green-900/20 rounded">
    <span className="text-green-400">Icon</span>
    <span className="text-sm text-green-300">
      Email verified
    </span>
  </div>
</div>`} />
          </div>

          {/* Variation 6: Toast Success */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Toast Notification</label>
            <button onClick={() => triggerSuccess('success6')} className="w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-sm font-semibold text-white rounded mb-2">Toast Success</button>
            {showSuccesses['success6'] && (
              <div className="fixed bottom-4 right-4 p-4 bg-emerald-900/90 border border-emerald-700 rounded-lg flex items-center gap-3 animate-slide-in z-50">
                <span className="text-2xl"><Icon name="party" className="w-6 h-6" /></span>
                <div>
                  <h4 className="font-semibold text-emerald-300">All Set!</h4>
                  <p className="text-xs text-emerald-200">Settings saved successfully</p>
                </div>
              </div>
            )}
            <CopyCode number_code={60} code={`{show && (
  <div className="fixed bottom-4 right-4 p-4 
    bg-emerald-900/90 rounded-lg 
    flex items-center gap-3">
    <span className="text-2xl">Icon</span>
    <div>
      <h4 className="font-semibold 
        text-emerald-300">All Set!</h4>
      <p className="text-xs text-emerald-200">
        Settings saved
      </p>
    </div>
  </div>
)}`} />
          </div>

          {/* Variation 7: Modal Success */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Modal Success</label>
            <button onClick={() => triggerSuccess('success7')} className="w-full px-3 py-2 bg-teal-600 hover:bg-teal-500 text-sm font-semibold text-white rounded mb-2">Show Modal</button>
            {showSuccesses['success7'] && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 mt-96">
                <div className="bg-slate-950 border border-teal-700/50 rounded-lg p-8 text-center max-w-sm">
                  <div className="text-6xl mb-4 flex justify-center"><Icon name="party" className="w-12 h-12" /></div>
                  <h3 className="text-xl font-bold text-teal-300 mb-2">Order Confirmed!</h3>
                  <p className="text-slate-400 mb-6">Your order #12345 has been confirmed. You'll receive tracking info soon.</p>
                  <button onClick={() => setShowSuccesses(prev => ({ ...prev, success7: false }))} className="px-6 py-2 bg-teal-600 hover:bg-teal-500 text-white font-semibold rounded">Continue</button>
                </div>
              </div>
            )}
            <CopyCode number_code={61} code={`<div className="fixed inset-0 bg-black/50 
  flex items-center justify-center z-50">
  <div className="bg-slate-950 border 
    border-teal-700/50 rounded-lg p-8 text-center">
    <div className="text-6xl mb-4">Icon</div>
    <h3 className="text-xl text-teal-300 font-bold">
      Order Confirmed!
    </h3>
    <p className="text-slate-400 mt-2 mb-6">
      Tracking info coming soon.
    </p>
  </div>
</div>`} />
          </div>

          {/* Variation 8: Card Success */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Card Success</label>
            <div className="p-4 bg-gradient-to-r from-green-900/20 to-emerald-900/20 border border-green-700/50 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="text-5xl"><Icon name="check" className="w-10 h-10" /></div>
                <div>
                  <h4 className="text-lg font-bold text-green-300">Congratulations!</h4>
                  <p className="text-green-200 mt-2">You've unlocked the Pro tier with all premium features.</p>
                  <div className="mt-3 space-y-1 text-sm text-green-200">
                    <p>- Unlimited projects</p>
                    <p>- Priority support</p>
                    <p>- Advanced analytics</p>
                  </div>
                </div>
              </div>
            </div>
            <CopyCode number_code={62} code={`<div className="p-4 bg-gradient-to-r 
  from-green-900/20 to-emerald-900/20 rounded">
  <div className="flex items-start gap-3">
    <div className="text-5xl">Icon</div>
    <div>
      <h4 className="text-lg font-bold 
        text-green-300">Congratulations!</h4>
      <p className="text-green-200 mt-2">
        You've unlocked Pro tier.
      </p>
    </div>
  </div>
</div>`} />
          </div>

        </div>
      </div>
    </div>
  );
};

export default SuccessMessage;


